/** The backend refused or failed to build the PDF; the message is safe to show to the user. */
export class PdfDownloadError extends Error {}

/**
 * Downloads a PDF from `url` without navigating the page, opening a popup or
 * using an iframe. Each of those is blocked or intercepted by the browser in
 * some situation — a popup/iframe download once the click's user activation
 * has expired (e.g. after a slow file upload), a top-level navigation by the
 * page's own "Leave site?" guard — whereas saving an in-memory blob via an
 * anchor click is none of them.
 *
 * If the server answers with an error (a Word file that can't be converted,
 * a merge failure, ...) this throws a PdfDownloadError carrying the server's
 * own message, so the caller can show it instead of landing on raw JSON.
 * Only when the request can't be made at all (e.g. blocked cross-origin) does
 * it fall back to plain navigation.
 */
export async function downloadPdfFromUrl(url: string, fallbackName = 'document.pdf'): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url);
  } catch {
    window.location.href = url;
    return;
  }

  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.error ?? '';
    } catch {
      /* not JSON */
    }
    throw new PdfDownloadError(detail || `The PDF could not be prepared (HTTP ${response.status}).`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const fileName = match?.[1] ?? fallbackName;

  const objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
